import React, { FC, useState, ReactNode } from 'react';
import { useQuery } from 'react-query';
import AudienceRestrictionsApi from 'app/modules/posts/api/audienceRestrictions';
import {
  AudienceRestrictionFieldOption,
  AudienceRestrictionsOptions,
} from 'app/modules/posts/types/AudienceRestrictionsOptions';
import { AudienceRestrictionField } from 'app/modules/posts/modules/detail/modules/audienceRestrictions/constants/audienceRestrictions';
import useDebounce from 'utils/hooks/useDebounce';
import { queryKey } from 'constants/queryKey';
import { Field, Select } from '@kontentino/ui';
import { useTranslation } from 'react-i18next';

type Props = {
  label: ReactNode;
  type: AudienceRestrictionField;
  pageType: number;
  isSingle?: boolean;
  onChange: (value: AudienceRestrictionsOptions) => void;
  initialValue?:
    | AudienceRestrictionFieldOption[]
    | AudienceRestrictionFieldOption;
  placeholder?: string;
};

const RestrictionSelect: FC<Props> = ({
  type,
  pageType,
  onChange,
  placeholder,
  label,
  isSingle,
  initialValue,
}) => {
  const { t } = useTranslation();
  const finalPlaceholder =
    placeholder ?? t('audienceRestrictions.searchPlaceholder');
  const [inputValue, setInputValue] = useState('');
  const debouncedSearch = useDebounce(inputValue, 500);

  const { data: searchData, isLoading } = useQuery(
    queryKey.pageAudienceRestrictions({
      term: debouncedSearch,
      type,
      pageType,
    }),
    () =>
      AudienceRestrictionsApi.searchOptions({
        term: debouncedSearch,
        type,
        pageType,
      }),
    {
      enabled:
        (isSingle && type === AudienceRestrictionField.ageMin) ||
        debouncedSearch?.length >= 3,
    },
  );

  return (
    <Field.Group>
      <>
        <Field.Label required>{label}</Field.Label>
        {isSingle && (
          <Select
            value={initialValue}
            isMulti={!isSingle}
            onChange={(newValues) => onChange({ [type]: [newValues] })}
            placeholder={finalPlaceholder}
            options={searchData}
            inputValue={inputValue}
            menuPortalTarget={document.body}
            styles={{
              menuPortal: (styles) => ({
                ...styles,
                zIndex: 1010,
              }),
            }}
          />
        )}
        {!isSingle && (
          <Select
            value={initialValue}
            isMulti
            onChange={(newValues) => onChange({ [type]: newValues })}
            placeholder={finalPlaceholder}
            closeMenuOnSelect={false}
            isLoading={isLoading}
            options={searchData}
            inputValue={inputValue}
            onInputChange={(value) => {
              setInputValue(value);
            }}
            blurInputOnSelect={false}
            noOptionsMessage={() =>
              !!inputValue
                ? t('noOptionsMessageWithInput')
                : t('noOptionsMessageInitial')
            }
            menuPortalTarget={document.body}
            styles={{
              menuPortal: (styles) => ({
                ...styles,
                zIndex: 1010,
              }),
            }}
          />
        )}
      </>
    </Field.Group>
  );
};

export default RestrictionSelect;
